<template>
    <ElDialog
        v-model="visible"
        :showClose="false"
        width="665px"
        class="ip-history-dialog"
        :appendToBody="true"
        @opened="handleDialogOpened"
    >
        <template #header>
            <div class="flex items-center justify-between w-full">
                <input
                    ref="searchInputRef"
                    v-model="searchQuery"
                    type="text"
                    :placeholder="t('history.searchPlaceholder')"
                    class="ip-history-input flex-1"
                    @keydown="handleKeyDown"
                >
                <button
                    type="button"
                    class="ip-history-close-btn th-btn th-btn-plain th-btn-sm th-btn-only-icon"
                    @click="closeDialog"
                >
                    <PtIcon icon="SolidCloseBig" class="text-xl fill-gray-450" />
                </button>
            </div>
        </template>

        <!-- History List -->
        <div class="ip-history-dialog-body">
            <div
                class="ip-history-dialog-body-new-tasks"
                :class="{ 'ip-history-dialog-body-new-tasks-selected': selectedIndex === -1 }"
                @click="handleNewTasks"
            >
                <IconNewTask class="w-6 h-6 fill-grey-450" />
                <span class="text-grey-450">{{ t('NavBar.NEW_TASKS') }}</span>
            </div>
            <template v-if="filteredHistoryMaps.length > 0">
                <div v-for="group in filteredHistoryMaps" :key="group.date" class="mt-5">
                    <div class="text-xs text-grey-90 mb-3 px-4">
                        {{ formatDateLabel(group.date) }}
                    </div>
                    <div
                        v-for="item in group.data"
                        :key="item.id"
                        class="ip-history-item"
                        :class="{ 'ip-history-item-selected': getItemGlobalIndex(item) === selectedIndex }"
                        @click="handleClickHistory(item)"
                    >
                        <div class="flex items-center gap-1 overflow-x-hidden">
                            <div class="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                                <img v-if="item.icon_url" :src="item.icon_url" class="w-5 h-5">
                                <component :is="getIconName(item.type)" v-else class="w-5 h-5" />
                            </div>
                            <div class="flex mr-1">
                                <div v-if="getAgentName(item.type)" class="text-sm shrink-0 text-grey-450 mr-1">
                                    {{ getAgentName(item.type) }}:
                                </div>
                                <div class="text-sm line-clamp-1 text-black" v-html="highlightText(item.title, searchQuery)" />
                            </div>
                            <!-- <button
                                type="button"
                                class="th-btn th-btn-plain th-btn-sm th-btn-only-icon flex-shrink-0"
                                @click.stop="handleRemoveHistory(item)"
                            >
                                <PtIcon icon="SolidDeleteStroke" class="text-base fill-gray-400 hover:fill-red-500" />
                            </button> -->
                        </div>
                    </div>
                </div>
            </template>
            <div v-else class="text-center py-12">
                <PtIcon icon="SolidNoDataTip" class="text-4xl fill-gray-300 mx-auto mb-3" />
                <p class="text-sm text-gray-400">
                    {{ t('history.empty') }}
                </p>
            </div>
        </div>

        <!-- <template #footer> -->
        <!-- <div class="flex justify-between items-center w-full">
                <button
                    v-if="props.historyList.length > 0"
                    type="button"
                    class="th-btn th-btn-text th-btn-sm"
                    @click="handleRemoveAll"
                >
                    {{ t('history.clearAll') }}
                </button>
                <div v-else></div>
                <button type="button" class="th-btn th-btn-sm" @click="closeDialog">
                    {{ t('history.close') }}
                </button>
            </div> -->
        <!-- </template> -->
    </ElDialog>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ElDialog } from 'element-plus';
import PtIcon from '@patsnap-biz/icon';
import IconNewTask from './icons/IconNewTask.vue';
import Hiro from './icons/Hiro.vue';
import Telecom from './icons/Telecom.vue';
import DesignFTOSearch from './icons/DesignFTOSearch.vue';
import AgentIcon from './icons/AgentIcon.vue';
import PLGIcon from './icons/PLGIcon.vue';
import ResearchIcon from './icons/ResearchIcon.vue';
import PatentDisclosure from './icons/PatentDisclosure.vue';
import RiskIcon from './icons/RiskIcon.vue';
import AIFTOSearchIcon from './icons/AIFTOSearchIcon.vue';
import AINoveltySearchIcon from './icons/AINoveltySearchIcon.vue';
import AIPatentDisclosureIcon from './icons/AIPatentDisclosureIcon.vue';
import AISpecificationIcon from './icons/AISpecificationIcon.vue';
import AITranslationIcon from './icons/AITranslationIcon.vue';
import TrizMindIcon from './icons/TrizMindIcon.vue';
import CompanyScoutIcon from './icons/CompanyScoutIcon.vue';
import RDTechMindIcon from './icons/TechMindIcon.vue';
import LSChatIcon from './icons/LSChatIcon.vue';
import { IMenuType } from '../types';
import { cn, en, jp, tw } from '../locales';
import '@patsnap-ui/icon/assets/solid/DeleteStroke.svg';
import '@patsnap-ui/icon/assets/solid/CloseBig.svg';
import '@patsnap-ui/icon/assets/solid/NoDataTip.svg';
import '../style/history-dialog.scss';

interface HistoryItem {
    id?: string;
    title: string;
    business_name?: string;
    icon_url?: string;
    type?: IMenuType;
}

interface HistoryGroup {
    date: string;
    data: HistoryItem[];
}

const props = defineProps<{
    historyMaps: HistoryGroup[];
    onClickHistory: (item: HistoryItem) => void;
    onRemoveHistory?: (item: HistoryItem) => void;
    onRemoveAll?: () => void;
    onNewTasks?: () => void;
}>();

const { t } = useI18n({
    messages: {
        cn,
        en,
        jp,
        tw,
    },
});

// 根据 type 获取 agent 名称
function getAgentName(type?: IMenuType): string {
    if (!type) return '';

    const agentNameMap: Record<string, string> = {
        [IMenuType.IP_Hiro]: t('histroySearch.Hiro'),
        [IMenuType.AI_NOVELTY_SEARCH]: t('histroySearch.NoveltySearch'),
        [IMenuType.AI_FTO_SEARCH]: t('histroySearch.FTOSearch'),
        [IMenuType.AI_NEW_FTO_SEARCH]: t('histroySearch.FTOSearch'),
        [IMenuType.AI_FTO_IMAGE]: t('histroySearch.DesignFTOSearch'),
        [IMenuType.IP_TELECOM_AICC]: t('histroySearch.Telecom'),
        [IMenuType.AI_PATENT_DISCLOSURE]: t('histroySearch.InventionDisclosure'),
        [IMenuType.PATENT_DISCLOSURE]: t('histroySearch.InventionDisclosure'),
    };

    return agentNameMap[type] || '';
}

const visible = ref(false);
const searchQuery = ref('');
const searchInputRef = ref<HTMLInputElement | null>(null);
const selectedIndex = ref(-1);

// 高亮匹配的文本
function highlightText(text: string, query: string): string {
    if (!query.trim()) {
        return text;
    }
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<span style="color: #0764E9;">$1</span>');
}

// 格式化日期显示
function formatDateLabel(dateStr: string): string {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const targetDate = new Date(dateStr);

    // 判断是否是今天
    if (
        targetDate.getFullYear() === today.getFullYear() &&
        targetDate.getMonth() === today.getMonth() &&
        targetDate.getDate() === today.getDate()
    ) {
        return t('history.today');
    }

    // 判断是否是昨天
    if (
        targetDate.getFullYear() === yesterday.getFullYear() &&
        targetDate.getMonth() === yesterday.getMonth() &&
        targetDate.getDate() === yesterday.getDate()
    ) {
        return t('history.yesterday');
    }

    // 其他日期返回原始字符串
    return dateStr;
}

// 获取图标组件
function getIconName(type?: IMenuType) {
    if (!type) return AgentIcon;

    switch (type) {
    case IMenuType.IP_Hiro:
        return Hiro;
    case IMenuType.IP_TELECOM_AICC:
        return Telecom;
    case IMenuType.AI_FTO_IMAGE:
        return DesignFTOSearch;
    case IMenuType.TECH_RESEARCH:
        return ResearchIcon;
    case IMenuType.TECH_FMEA:
        return RiskIcon;
    case IMenuType.PATENT_DISCLOSURE:
        return PatentDisclosure;
    case IMenuType.AI_TRANSLATION:
        return AITranslationIcon;
    case IMenuType.AI_SPECIFICATION:
        return AISpecificationIcon;
    case IMenuType.AI_PATENT_DISCLOSURE:
        return AIPatentDisclosureIcon;
    case IMenuType.AI_NOVELTY_SEARCH:
        return AINoveltySearchIcon;
    case IMenuType.AI_FTO_SEARCH:
    case IMenuType.AI_NEW_FTO_SEARCH:
        return AIFTOSearchIcon;
    case IMenuType.TRIZ_MIND:
        return TrizMindIcon;
    case IMenuType.COMPANY_SCOUT:
        return CompanyScoutIcon;
    case IMenuType.PLG_QA:
        return PLGIcon;
    case IMenuType.RD_TECH_MIND:
        return RDTechMindIcon;
    case IMenuType.LS_CHAT:
        return LSChatIcon;
    default:
        return AgentIcon;
    }
}

// 允许显示的历史记录类型
const allowedTypes = [
    IMenuType.IP_Hiro,
    IMenuType.IP_TELECOM_AICC,
    IMenuType.AI_FTO_IMAGE,
    IMenuType.AI_NOVELTY_SEARCH,
    IMenuType.AI_NEW_FTO_SEARCH,
    IMenuType.AI_FTO_SEARCH,
];

// 过滤历史记录（在分组数据上进行搜索和类型过滤）
const filteredHistoryMaps = computed(() => {
    const query = searchQuery.value.toLowerCase();
    return props.historyMaps
        .map(group => ({
            date: group.date,
            data: group.data.filter(item => {
                // 类型过滤：只显示允许的类型
                // if (item.type && !allowedTypes.includes(item.type)) {
                //     return false;
                // }
                // 搜索过滤：如果有搜索词，则进行搜索匹配
                if (query.trim()) {
                    return item.title?.toLowerCase().includes(query) ||
                           item.business_name?.toLowerCase().includes(query);
                }
                return true;
            }),
        }))
        .filter(group => group.data.length > 0);
});

// 获取所有历史记录项的扁平列表
const flatHistoryList = computed(() => {
    return filteredHistoryMaps.value.flatMap(group => group.data);
});

// 监听搜索内容变化，重置选中索引
watch(searchQuery, () => {
    selectedIndex.value = -1;
});

// 获取历史记录项的全局索引
function getItemGlobalIndex(item: HistoryItem): number {
    return flatHistoryList.value.findIndex(i => i.id === item.id);
}

function closeDialog() {
    visible.value = false;
}

function openDialog() {
    visible.value = true;
}

function handleDialogOpened() {
    setTimeout(() => {
        searchInputRef.value?.focus();
    }, 200);
}

function handleNewTasks() {
    if (props.onNewTasks) {
        props.onNewTasks();
    } else {
        window.location.href = '/ip/#/home';
    }
    closeDialog();
}

function handleClickHistory(item: HistoryItem) {
    props.onClickHistory(item);
    closeDialog();
}

// 滚动到选中项
function scrollToSelectedItem() {
    nextTick(() => {
        let selectedElement;
        if (selectedIndex.value === -1) {
            selectedElement = document.querySelector('.ip-history-dialog-body-new-tasks-selected');
        } else {
            selectedElement = document.querySelector('.ip-history-item-selected');
        }
        if (selectedElement) {
            selectedElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    });
}

// 键盘事件处理
function handleKeyDown(event: KeyboardEvent) {
    const list = flatHistoryList.value;

    if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (list.length === 0) return;
        selectedIndex.value = Math.min(selectedIndex.value + 1, list.length - 1);
        scrollToSelectedItem();
    } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        selectedIndex.value = Math.max(selectedIndex.value - 1, -1);
        scrollToSelectedItem();
    } else if (event.key === 'Enter') {
        event.preventDefault();
        if (selectedIndex.value === -1) {
            handleNewTasks();
        } else if (selectedIndex.value >= 0) {
            const selectedItem = list[selectedIndex.value];
            if (selectedItem) {
                handleClickHistory(selectedItem);
            }
        }
    }
}

// function handleRemoveHistory(item: HistoryItem) {
//     props.onRemoveHistory(item);
// }

// function handleRemoveAll() {
//     props.onRemoveAll();
// }

defineExpose({
    openDialog,
    closeDialog,
});
</script>
